// @ts-nocheck
import { z } from 'zod';

import { removeSpacesFromString } from '../../../services/general.service';
import { FormTypes, ISupportedCountry, MoveMoneyTypes, UpdateVendorFormFieldsMap } from '../interfaces';
import { ValidateFieldTypes, validationSchemeMap } from '../../../helpers/inputUtils.helper';
import { isBusinessOrRecipientNameValid } from '../../MoveMoney/utils';

const getBaseScheme = (formType: FormTypes) => z.object({
  /** Base */
  [UpdateVendorFormFieldsMap.BUSINESS_NAME]: z
    .string()
    .trim()
    .min(1, formType === FormTypes.BUSINESS ? 'Business Name is required' : 'Recipient Name is required')
    .min(5, 'Minimum 5 characters')
    .max(40, 'Maximum 40 characters')
    .refine((value) => !/\s{2,}/.test(value), 'Please enter a valid name')
    .refine((value) => /^[a-zA-Z0-9 ]+$/.test(value), 'Please enter a valid name')
    .refine((value) => isBusinessOrRecipientNameValid(value), formType === FormTypes.BUSINESS ? 'Business name must include at least two words (e.g., Lili Inc).' : 'Name must include both a first and last name'),
  [UpdateVendorFormFieldsMap.EMAIL]: z
    .string()
    .trim()
    .refine((value) => {
      if (!value) return true;
      return validationSchemeMap[ValidateFieldTypes.EMAIL].test(value);
    }, 'Invalid email'),
  [UpdateVendorFormFieldsMap.NOTIFY_VENDOR]: z.boolean().optional(),
  /** --- End Base --- */
});

const getInternationalWireScheme = (
  moveType: MoveMoneyTypes | undefined,
  selectedCountry: ISupportedCountry | null,
  isInternationalWireRequired: boolean,
) => {
  const shouldValidateIban = () => {
    const valueToParse = selectedCountry?.ibanSpecificValidationRequired || 'false';
    try {
      return JSON.parse(valueToParse);
    } catch (error) {
      console.error('Error in shouldValidateIban', error);
      return false;
    }
  };

  const swiftRegex = selectedCountry?.swiftCodeValidationRegex
    ? new RegExp(selectedCountry?.swiftCodeValidationRegex)
    : /^.+$/;
  const ibanRegex =
    selectedCountry?.ibanValidationRegex && shouldValidateIban()
      ? new RegExp(selectedCountry?.ibanValidationRegex)
      : /^[a-zA-Z0-9]{5,34}$/;
  const isRequired = moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT || isInternationalWireRequired;

  if (isRequired) {
    return z.object({
      [UpdateVendorFormFieldsMap.INTER_PHONE_STATUS]: z.boolean().refine((value) => value),
      [UpdateVendorFormFieldsMap.INTER_COUNTRY]: z.string().min(1),
      [UpdateVendorFormFieldsMap.SWIFT]: z
        .string()
        .trim()
        .min(1, 'SWIFT code is required')
        .regex(swiftRegex, 'SWIFT code is invalid'),
      [UpdateVendorFormFieldsMap.IBAN]: z
        .string()
        .trim()
        .min(1, 'IBAN code is required')
        .refine((value) => ibanRegex.test(removeSpacesFromString(value)), 'IBAN code is invalid'),
    });
  }
  return z.object({});
};

const getAchOutTransferScheme = (moveType: MoveMoneyTypes | undefined, isAchRequired:boolean) => {
  const isRequired = moveType === MoveMoneyTypes.ACH_OUT_TRANSFER || isAchRequired;

  if (isRequired) {
    return z.object({
      [UpdateVendorFormFieldsMap.ACH_ROUTING_NUMBER]: z
        .string()
        .trim()
        .min(1, 'Routing number is required')
        .regex(/^[0-9]{9}$/, 'Routing number is invalid'),
      [UpdateVendorFormFieldsMap.ACH_ACCOUNT_NUMBER]: z
        .string()
        .trim()
        .min(1, 'Account number is required')
        .regex(/^[0-9]{5,17}$/, 'Account number is invalid'),
    });
  }
  return z.object({});
};

const getDomesticWireTransferScheme = (
  moveType: MoveMoneyTypes | undefined,
  isDomesticWireRequired: boolean,
) => {
  const isRequired = moveType === MoveMoneyTypes.WIRE_OUT || isDomesticWireRequired;

  if (isRequired) {
    return z.object({
      [UpdateVendorFormFieldsMap.WIRE_ROUTING_NUMBER]: z
        .string()
        .trim()
        .min(1, 'Routing number is required')
        .regex(/^[0-9]{9}$/, 'Routing number is invalid'),
      [UpdateVendorFormFieldsMap.WIRE_ACCOUNT_NUMBER]: z
        .string()
        .trim()
        .min(1, 'Account number is required')
        .regex(/^[0-9]{5,17}$/, 'Account number is invalid'),
    });
  }

  return z.object({});
};

const getDomesticAddressScheme = (
  moveType: MoveMoneyTypes | undefined,
  isDomesticAddressRequired: boolean,
) => {
  const isRequired =
    moveType === MoveMoneyTypes.WIRE_OUT ||
    moveType === MoveMoneyTypes.MAIL_CHECK || isDomesticAddressRequired;

  if (isRequired) {
    return z.object({
      [UpdateVendorFormFieldsMap.LINE_1]: z
        .string()
        .trim()
        .min(1, 'Address is required')
        .regex(/^[A-Za-z0-9-\s]+$/, 'Address is invalid'),
      [UpdateVendorFormFieldsMap.LINE_2]: z.string().trim().optional(),
      [UpdateVendorFormFieldsMap.CITY]: z
        .string()
        .trim()
        .min(1, 'City is required')
        .regex(/^[A-Za-z\s]+$/, 'City is invalid'),
      [UpdateVendorFormFieldsMap.POSTAL_CODE]: z
        .string()
        .trim()
        .min(1, 'Postal code is required')
        .regex(/^[0-9]{5}$/, 'Postal code is invalid'),
      [UpdateVendorFormFieldsMap.STATE]: z
        .string()
        .trim()
        .refine((value) => !!value, 'State is required'),
    });
  }

  return z.object({});
};

const getInternationalAddressScheme = (
  moveType: MoveMoneyTypes | undefined,
  selectedCountry: ISupportedCountry | null,
  isInternationalAddressRequired: boolean,
) => {
  const isRequired = moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT || isInternationalAddressRequired;
  const zipCodeRegex = selectedCountry?.postalCodeValidationRegex
    ? new RegExp(selectedCountry?.postalCodeValidationRegex)
    : /^\d{5,7}$/;

  const zipCodeRegexError = selectedCountry?.postalCodeValidationDescription || 'Postal code is invalid';

  const isStateOptional = !selectedCountry?.subDivisions.length;

  if (isRequired) {
    return z.object({
      [UpdateVendorFormFieldsMap.INTER_LINE_1]: z
        .string()
        .trim()
        .min(1, 'Address is required')
        .min(2, 'Minimum 2 characters')
        .refine((value) => value.length >= 1, 'Address is invalid'),
      [UpdateVendorFormFieldsMap.INTER_LINE_2]: z.string().trim().optional(),
      [UpdateVendorFormFieldsMap.INTER_CITY]: z
        .string()
        .trim()
        .min(1, 'City is required')
        .refine((value) => {
          if (value) return /^[A-Za-z\s]+$/.test(value);
          return true;
        }, 'City is invalid'),
      [UpdateVendorFormFieldsMap.INTER_POSTAL_CODE]: z
        .string()
        .trim()
        .min(1, 'Postal code is required')
        .refine((value) => {
          if (value) return zipCodeRegex.test(value);
          return true;
        }, zipCodeRegexError),
      [UpdateVendorFormFieldsMap.INTER_STATE]: z
        .string()
        .trim()
        .refine((value) => {
          if (isStateOptional) {
            return true;
          }
          return !!value;
        }, 'State is required'),
    });
  }

  return z.object({});
};

export const getUpdateVendorFormScheme = (
  moveType: MoveMoneyTypes | undefined,
  selectedCountry: ISupportedCountry | undefined,
  formType: FormTypes,
  isDomesticAddressRequired: boolean,
  isDomesticWireRequired: boolean,
  isAchRequired: boolean,
  isInternationalAddressRequired: boolean,
  isInternationalWireRequired: boolean,
  isUpdateNameRequired?: boolean,
  isVendorUpdateMandatory?: boolean,
) => {
  if (!isVendorUpdateMandatory && isUpdateNameRequired) {
    return getBaseScheme(formType);
  }
  return getBaseScheme(formType)
    .merge(getDomesticWireTransferScheme(moveType, isDomesticWireRequired))
    .merge(getInternationalWireScheme(moveType, selectedCountry, isInternationalWireRequired))
    .merge(getInternationalAddressScheme(moveType, selectedCountry, isInternationalAddressRequired))
    .merge(getAchOutTransferScheme(moveType, isAchRequired))
    .merge(getDomesticAddressScheme(moveType, isDomesticAddressRequired));
};
